import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, Mail, MousePointer, Eye, Users, Clock,
  ChevronLeft, ExternalLink, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat card for campaign metrics
const MetricCard = ({ icon: Icon, label, value, subValue, color = 'gold' }) => (
  <div className="bg-[#0a0d14]/50 rounded-xl p-4 sm:p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs sm:text-sm mb-1">{label}</p>
        <p className={`text-2xl sm:text-3xl font-bold ${color === 'gold' ? 'text-[#d4af37]' : 'text-white'}`}>
          {value}
        </p>
        {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
      </div>
      <div className={`p-2 sm:p-3 rounded-lg ${color === 'gold' ? 'bg-[#d4af37]/10' : 'bg-gray-800'}`}>
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${color === 'gold' ? 'text-[#d4af37]' : 'text-gray-400'}`} />
      </div>
    </div>
  </div>
);

const CampaignAnalytics = ({ onBack }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch campaigns list
  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/campaigns?limit=50`, {
        withCredentials: true
      });
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/campaigns/${campaignId}/analytics`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching campaign analytics:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const selectCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    fetchCampaignAnalytics(campaign.id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#d4af37]/30 border-t-[#d4af37] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  // Campaign detail view
  if (selectedCampaign && analytics) {
    const { stats } = analytics;
    
    return (
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => { setSelectedCampaign(null); setAnalytics(null); }}
          className="flex items-center gap-2 text-gray-400 hover:text-[#d4af37] transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Back to Campaigns</span>
        </button>

        {/* Campaign Header */}
        <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-serif text-white mb-2">{analytics.subject}</h2>
              <p className="text-sm text-gray-400">Sent {formatDate(analytics.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                Completed
              </span>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <MetricCard
            icon={Users}
            label="Sent"
            value={stats.sent}
            subValue={`${stats.failed} failed`}
          />
          <MetricCard
            icon={Eye}
            label="Opened"
            value={stats.opened}
            subValue={`${stats.open_rate}% open rate`}
            color="white"
          />
          <MetricCard
            icon={MousePointer}
            label="Clicked"
            value={stats.clicked}
            subValue={`${stats.click_rate}% click rate`}
            color="white"
          />
          <MetricCard
            icon={BarChart3}
            label="Click-to-Open"
            value={`${stats.click_to_open_rate}%`}
            subValue="of openers clicked"
            color="white"
          />
        </div>

        {/* Click Breakdown */}
        {Object.keys(analytics.click_breakdown || {}).length > 0 && (
          <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-5 sm:p-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-[#d4af37]" />
              Link Performance
            </h3>
            <div className="space-y-3">
              {Object.entries(analytics.click_breakdown).map(([url, clicks]) => (
                <div key={url} className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm truncate max-w-[300px] sm:max-w-[500px]">{url}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 sm:w-32 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#d4af37] rounded-full"
                        style={{ width: `${(clicks / stats.clicked) * 100}%` }}
                      />
                    </div>
                    <span className="text-[#d4af37] text-sm font-medium w-8 text-right">{clicks}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {analytics.recent_events?.length > 0 && (
          <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-5 sm:p-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#d4af37]" />
              Recent Activity
            </h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {analytics.recent_events.slice(0, 20).map((event, idx) => (
                <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-800/50 last:border-0">
                  {event.type === 'open' ? (
                    <Eye className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  ) : (
                    <MousePointer className="w-4 h-4 text-green-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{event.email}</p>
                    {event.url && (
                      <p className="text-xs text-gray-500 truncate">{event.url}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {formatShortDate(event.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recipient Details */}
        <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-5 sm:p-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#d4af37]" />
            Recipient Details
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0d14]/50">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-3 py-2">Email</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-3 py-2">Status</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-3 py-2">Opened</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-3 py-2">Clicks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {analytics.recipient_details?.map((r, idx) => (
                  <tr key={idx} className="hover:bg-[#0a0d14]/30">
                    <td className="px-3 py-3">
                      <div>
                        <p className="text-sm text-white">{r.name || '-'}</p>
                        <p className="text-xs text-gray-500">{r.email}</p>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {r.status === 'sent' ? (
                        <span className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-xs">
                          <XCircle className="w-3 h-3" /> Failed
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {r.opened ? (
                        <span className="text-blue-400 text-xs">{formatShortDate(r.opened_at)}</span>
                      ) : (
                        <span className="text-gray-500 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs ${r.click_count > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                        {r.click_count || 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Campaign list view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-serif text-white">Campaign Analytics</h2>
        <p className="text-sm text-gray-400">{campaigns.length} campaigns</p>
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-12 text-center">
          <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">No campaigns yet</h3>
          <p className="text-gray-400 text-sm">Send your first campaign to see analytics here.</p>
        </div>
      ) : (
        <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0d14]/50">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Campaign</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Sent</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Opens</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Clicks</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {campaigns.map(campaign => (
                  <tr
                    key={campaign.id}
                    onClick={() => selectCampaign(campaign)}
                    className="hover:bg-[#0a0d14]/30 cursor-pointer transition-colors"
                    data-testid={`campaign-row-${campaign.id}`}
                  >
                    <td className="px-4 py-4">
                      <p className="text-white font-medium truncate max-w-[250px]">{campaign.subject}</p>
                      <p className="text-xs text-gray-500">{campaign.recipient_count} recipients</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-sm ${campaign.sent_count > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                        {campaign.sent_count}
                      </span>
                      {campaign.failed_count > 0 && (
                        <span className="text-xs text-red-400 ml-1">({campaign.failed_count} failed)</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white">{campaign.open_count || 0}</span>
                        <span className="text-xs text-[#d4af37]">({campaign.open_rate || 0}%)</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white">{campaign.click_count || 0}</span>
                        <span className="text-xs text-[#d4af37]">({campaign.click_rate || 0}%)</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-400">{formatShortDate(campaign.created_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analytics Legend */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-300">
            <p className="font-medium mb-1">About Campaign Tracking</p>
            <p className="text-blue-300/80">
              Open rates are tracked when recipients load images in the email. Click rates are tracked when recipients click links.
              Some email clients block tracking, so actual engagement may be higher than reported.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;
